/*
 * The rules behind a PaneSwitch, kept apart from its component so they can be checked without a
 * browser: which of its two panes the page's address shows, the address that shows the other, how
 * many of what arrives in a pane arrived while it was out of sight, and how tall the column stands
 * on `compact`.
 */

/** The search parameter that names the pane shown, when it isn't the first. */
export const paneParam = "pane"

/**
 * The pane `search` shows: the second when the parameter names it, and otherwise the first, as an
 * address with no parameter, or one naming no pane of these, does.
 */
export function paneOf<Pane extends string>(
  search: URLSearchParams,
  [first, second]: readonly [Pane, Pane],
): Pane {
  return search.get(paneParam) === second ? second : first
}

/**
 * `search` showing `pane`, every other parameter kept: the first pane is the address without the
 * parameter, so the page's own address shows it.
 */
export function withPane<Pane extends string>(
  search: URLSearchParams,
  pane: Pane,
  [first]: readonly [Pane, Pane],
) {
  const next = new URLSearchParams(search)
  if (pane === first) next.delete(paneParam)
  else next.set(paneParam, pane)
  return next
}

/**
 * How many of a pane's `arrived` items came after it was last in view, when `seen` had arrived; a
 * total that fell (a message taken back) leaves none unseen.
 */
export const unseenOf = (arrived: number, seen: number) => Math.max(arrived - seen, 0)

/**
 * How tall a compact column stands, and how far its foot runs over the padding under it, in px:
 * the page scroll's `screen` height less the stuck chrome `above` it and what the page keeps after
 * it (`trailing`). When all of `trailing` is the `padding` of what holds the column (the body's,
 * the page scroll's), nothing follows to make room for, so the column runs over it down to the
 * bars `docked` at the screen's foot, or to the foot with none docked.
 */
export function columnFit({
  screen,
  above,
  trailing,
  padding,
  docked,
}: {
  screen: number
  above: number
  trailing: number
  padding: number
  docked: number
}) {
  const overlap = trailing - padding < 1 ? Math.max(trailing - docked, 0) : 0
  return { height: Math.max(screen - above - trailing + overlap, 0), overlap }
}
